import { ref, watch, onMounted, onUnmounted } from 'vue';
import { calculatePackagePrice } from '../components/configurator/utils/priceUtils.js';

const DEBOUNCE_DELAY = 500;

export function useAutoSave(
  configData,
  flowType,
  currentStepIndex,
  STORAGE_KEY = 's7_configuration_draft',
) {
  const isSaving = ref(false);
  const lastSaved = ref(null);
  const saveError = ref(null);
  const hasUnsavedChanges = ref(false);
  const showPackageTypeModal = ref(false);

  let saveTimeout = null;

  const saveConfiguration = async () => {
    if (!configData.value) return;

    // Do not save since basePackage is empty
    if (
      configData.value.isBasePackage &&
      configData.value.basePackage === null
    ) {
      // Clear draft, since draft is empty
      clearDraft();
      return;
    }

    // Do not save since products are empty
    if (
      !configData.value.isBasePackage &&
      configData.value.packaging === null &&
      configData.value.packagingProduct === null &&
      configData.value.products?.length < 1 &&
      configData.value.foodDrinks?.length < 1
    ) {
      // Clear draft, since draft is empty
      clearDraft();
      return;
    }

    // Try to save
    try {
      isSaving.value = true;
      saveError.value = null;

      const draftData = {
        flowType: flowType.value,
        step: currentStepIndex.value,
        quantity: configData.value.quantity,
        packageType: configData.value.packageType,
        products: configData.value.products || [],
        foodDrinks: configData.value.foodDrinks || [],
        packaging: configData.value.packaging,
        packagingOptions: configData.value.packagingOptions || [],
        packagingMode: configData.value.packagingMode,
        packagingProduct: configData.value.packagingProduct,
        personalMessage: configData.value.personalMessage || '',
        personalization: configData.value.personalization || {
          productPersonalization: {},
          personalMessage: '',
        },
        deliveryInfo: configData.value.deliveryInfo || {},
        basePackage: configData.value.basePackage,
        isBasePackage: configData.value.isBasePackage,
        isMailboxSession: configData.value.isMailboxSession || false,
        timestamp: Date.now(),
        isComplete: false,
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(draftData));
      lastSaved.value = new Date();
      hasUnsavedChanges.value = false;
    } catch {
      saveError.value = 'Failed to save configuration';
    } finally {
      isSaving.value = false;
    }
  };

  const debouncedSave = () => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }

    hasUnsavedChanges.value = true;
    saveTimeout = setTimeout(saveConfiguration, DEBOUNCE_DELAY);
  };

  const clearDraft = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      hasUnsavedChanges.value = false;
      lastSaved.value = null;
    } catch (error) {
      // Silent fail for production
      console.error('Error clearing drafts', error);
    }
  };

  const loadDraft = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const draftData = JSON.parse(stored);

        if (draftData.flowType === flowType.value) {
          return draftData;
        }
      }
    } catch (error) {
      // Silent fail for production
      console.error('Error loading drafts', error);
    }
    return null;
  };

  const getDraftSummary = async () => {
    const draft = loadDraft();
    if (!draft) return null;

    const productCount = (draft.products || []).filter(
      (p) => (p.count || 0) > 0,
    ).length;
    const foodCount = (draft.foodDrinks || []).filter(
      (f) => (f.count || 0) > 0,
    ).length;
    const totalItems = productCount + foodCount;

    let estimatedPrice = 0;
    try {
      estimatedPrice = await calculatePackagePrice(draft);
      if (isNaN(estimatedPrice)) {
        estimatedPrice = 0;
      }
    } catch {
      estimatedPrice = 0;
    }

    return {
      flowType: draft.flowType,
      step: draft.step,
      quantity: draft.quantity,
      totalItems,
      estimatedPrice,
      timestamp: draft.timestamp,
      hasPackaging: !!draft.packaging,
      hasPersonalization:
        !!(
          draft.personalization?.productPersonalization &&
          Object.keys(draft.personalization.productPersonalization).length > 0
        ) || !!draft.personalMessage,
    };
  };

  const restoreDraft = () => {
    const draft = loadDraft();
    if (!draft) return false;

    try {
      Object.assign(configData.value, {
        quantity: draft.quantity,
        packageType: draft.packageType,
        products: draft.products || [],
        foodDrinks: draft.foodDrinks || [],
        packaging: draft.packaging,
        packagingOptions: draft.packagingOptions || [],
        packagingMode: draft.packagingMode || 'box',
        packagingProduct: draft.packagingProduct,
        personalMessage: draft.personalMessage || '',
        personalization: draft.personalization || {
          productPersonalization: {},
          personalMessage: '',
        },
        deliveryInfo: draft.deliveryInfo || {},
        basePackage: draft.basePackage,
        isBasePackage: draft.isBasePackage,
        isMailboxSession: draft.isMailboxSession || false,
      });

      currentStepIndex.value = draft.step || 0;
      hasUnsavedChanges.value = false;
      showPackageTypeModal.value = false;

      return true;
    } catch (error) {
      console.error('Error restoring drafts', error);
      return false;
    }
  };

  const formatLastSaved = () => {
    if (!lastSaved.value) return null;
    return lastSaved.value.toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  watch(
    () => configData.value,
    () => {
      debouncedSave();
    },
    { deep: true },
  );

  watch(
    () => currentStepIndex.value,
    () => {
      debouncedSave();
    },
  );

  onMounted(() => {
    const draft = loadDraft();
    if (draft && draft.flowType === flowType.value) {
      lastSaved.value = new Date(draft.timestamp);
    }
  });

  onUnmounted(() => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
  });

  return {
    isSaving,
    lastSaved,
    saveError,
    hasUnsavedChanges,
    showPackageTypeModal,
    saveConfiguration,
    clearDraft,
    loadDraft,
    getDraftSummary,
    restoreDraft,
    formatLastSaved,
  };
}
